// "Send to …?" before a quotation / proforma / invoice goes to a customer
// (client, 2026-10-29: "galat insaan ko mail nahi jaana chahiye"). Shows the
// exact name, email and WhatsApp number the server will use — the same lookup
// as the send (GET /api/documents/:kind/:id/recipient) — and sends only on
// "Send".
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Mail, MessageCircle } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { Spinner } from '@/shared/components/ui/Spinner';
import { api } from '@/shared/api/axios';

export interface SendTarget {
  kind: 'sales-doc' | 'invoice';
  id: string;
  /** e.g. "Quotation RBM/26-27/6281" */
  label: string;
}

interface Recipient { name: string; email: string; phone: string; number: string }

export function SendConfirmDialog({ target, sending, onCancel, onConfirm }: {
  target: SendTarget | null;
  sending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['document-recipient', target?.kind, target?.id],
    enabled: Boolean(target),
    staleTime: 0,
    queryFn: async () => (await api.get<Recipient>(`/api/documents/${target!.kind}/${target!.id}/recipient`)).data,
  });
  if (!target) return null;
  const nothing = data && !data.email && !data.phone;

  return (
    <Modal
      open
      onClose={onCancel}
      size="md"
      title={`Send ${target.label}${data?.number && !target.label.includes(data.number) ? ` ${data.number}` : ''}?`}
      footer={(
        <>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={onConfirm} disabled={isLoading || isError || !data || nothing || sending}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </>
      )}
    >
      {isLoading && <div className="flex justify-center py-6"><Spinner /></div>}
      {isError && <p className="text-body-sm text-danger-text">Could not look up who receives this. Try again.</p>}
      {data && (
        <div className="space-y-3 text-body-sm">
          <p className="text-on-surface-variant">It will be sent to:</p>
          <p className="text-body-md font-semibold text-on-surface">{data.name || 'Customer'}</p>
          <div className="space-y-1.5 rounded-lg border border-outline p-3">
            <p className="flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0 text-on-surface-variant" />
              {data.email ? <span className="break-all font-medium">{data.email}</span> : <span className="text-on-surface-variant">No email on file — email will not be sent</span>}
            </p>
            <p className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 shrink-0 text-on-surface-variant" />
              {data.phone ? <span className="font-medium">WhatsApp {data.phone}</span> : <span className="text-on-surface-variant">No phone on file — WhatsApp will not be sent</span>}
            </p>
          </div>
          {nothing ? (
            <p className="flex items-center gap-2 text-danger-text"><AlertTriangle className="h-4 w-4" /> This customer has no email or phone on file. Add one in Contacts first.</p>
          ) : (
            <p className="text-on-surface-variant">Not the right person? Cancel, and correct the customer&apos;s email or phone in Contacts first.</p>
          )}
        </div>
      )}
    </Modal>
  );
}
